import { useQuery } from '@apollo/client';
import { Icon, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import COURSES_FRAME from 'gql/queries/InternationalOffice/getIOCoursesFrame.gql';

import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { FilterProps } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { IOCourse } from 'types/AppTypes';

export const IOCourseIframeList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const keywordState = useState();
  const semesterState = useState<string>('');
  const awpmState = useState<boolean>();
  const degreeState = useState<string>('');
  const facultyState = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const couresQuery = useQuery<
    any,
    {
      offset?: number;
      limit?: number;
      filter?: Object;
    }
  >(COURSES_FRAME, {
    variables: {
      offset: 0,
      limit: paginationModel.pageSize
    },
    fetchPolicy: 'no-cache'
  });

  const { refetch } = couresQuery;

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const filters = (ioFaculties): FilterProps['filterConfig'] => {
    const facultySelectOptions: [{ label: string; name: string }] = [{ label: '---', name: '' }];

    ioFaculties
      .sort((a, b) =>
        a?.toLowerCase() > b?.toLowerCase() ? 1 : a?.toLowerCase() < b?.toLowerCase() ? -1 : 0
      )
      .forEach(faculty => {
        facultySelectOptions.push({
          label: faculty,
          name: faculty
        });
      });

    const degreeOptions: { label: string; name: string }[] = [
      { label: '---', name: '' },
      { label: 'Bachelor', name: 'Bachelor' },
      { label: 'Master', name: 'Master' }
    ];

    const semesterOptions = [
      { label: '---', name: '' },
      { label: t(translations.pages.internationalOffice.courses.isSummer()), name: 'Summer' },
      { label: t(translations.pages.internationalOffice.courses.isWinter()), name: 'Winter' }
    ];

    return [
      {
        field: 'keyword',
        label: t(translations.pages.internationalOffice.users.search()),
        stateHandler: keywordState
      },
      {
        field: 'degree',
        label: t(translations.pages.internationalOffice.courses.degree()),
        stateHandler: degreeState,
        select: {
          options: degreeOptions,
          stateHandler: degreeState
        }
      },
      {
        field: 'faculty',
        label: t(translations.pages.internationalOffice.courses.faculty()),
        stateHandler: facultyState,
        select: {
          options: facultySelectOptions,
          stateHandler: facultyState
        }
      },
      {
        field: 'semester',
        label: t(translations.pages.internationalOffice.courses.semester()),
        filterSetter: (value, curFilters) => {
          const filters = { ...curFilters };
          if (filters['isWinter']) delete filters.isWinter;
          if (filters['isSummer']) delete filters.isSummer;
          if (value === 'Summer') filters.isSummer = true;
          if (value === 'Winter') filters.isWinter = true;
          return filters;
        },
        stateHandler: semesterState,
        select: {
          options: semesterOptions,
          stateHandler: semesterState
        }
      },
      {
        field: 'isAWPM',
        label: t(translations.pages.internationalOffice.courses.isAWPM()),
        stateHandler: awpmState,
        switch: {
          label: t(translations.pages.internationalOffice.courses.isAWPM()),
          stateHandler: awpmState
        }
      }
    ];
  };

  const columns = useMemo((): TypedGridColumns<IOCourse & { semester: string }> => {
    return [
      {
        flex: 2,
        minWidth: 250,
        headerName: t(translations.pages.internationalOffice.courses.faculty()),
        field: 'faculty',
        renderCell: params => (
          <Tooltip title={params.row.faculty}>
            <span>{params.row.faculty}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 300,
        headerName: t(translations.pages.internationalOffice.courses.courseName()),
        field: 'courseName',
        renderCell: params => (
          <Tooltip title={params.row.courseName}>
            <span>{params.row.courseName}</span>
          </Tooltip>
        )
      },
      {
        width: 100,
        headerName: t(translations.pages.internationalOffice.courses.courseNumber()),
        field: 'courseNumber'
      },
      {
        width: 120,
        headerName: t(translations.pages.internationalOffice.courses.semester()),
        field: 'semester',
        valueGetter: (value, row) => {
          if (row?.isSummer && row?.isWinter) {
            return `${t(translations.pages.internationalOffice.courses.isWinter())}/${t(
              translations.pages.internationalOffice.courses.isSummer()
            )}`;
          }
          if (row?.isSummer) {
            return t(translations.pages.internationalOffice.courses.isSummer());
          }
          if (row.isWinter) {
            return t(translations.pages.internationalOffice.courses.isWinter());
          }
          return '';
        }
      },
      {
        width: 100,
        headerName: t(translations.pages.internationalOffice.courses.degree()),
        field: 'degree'
      },
      {
        width: 100,
        type: 'boolean',
        headerName: t(translations.pages.internationalOffice.courses.isAWPMHeader()),
        field: 'isAWPM'
      },
      {
        width: 140,
        headerName: t(translations.pages.internationalOffice.courses.ects()),
        field: 'ectCount',
        align: 'center',
        headerAlign: 'center'
      },
      {
        width: 80,
        field: 'courseDescription',
        type: 'actions',
        align: 'center',
        headerName: t(translations.pages.internationalOffice.courses.courseDescription()),
        renderCell: (params: GridRenderCellParams) =>
          params.row.courseDescription ? (
            <a
              href={`${import.meta.env.VITE_GATEWAY_URL}/course/${params.row.id}/description`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon className="far fa-file-pdf fa-3x" />
            </a>
          ) : (
            <Icon
              className="fas fa-ban fa-3x"
              style={{
                color: '#aaa'
              }}
            />
          )
      }
    ];
  }, [t]);

  return (
    <GQLGuard
      query={[couresQuery]}
      onError={error => {
        dispatch(
          AlertActions.error(
            `${t(translations.pages.internationalOffice.courses.alerts.error.loadingCourses())}`
          )
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([coursesData]) => {
        const coursesCount = coursesData?.ioCoursesCount;

        return (
          <DataGrid
            slotProps={{
              toolbar: {
                filters: {
                  filterConfig: filters(coursesData?.faculties),
                  onFilterChange: refetch
                }
              }
            }}
            rows={coursesData?.ioCourses || []}
            disableColumnFilter
            disableRowSelectionOnClick
            columns={columns.map(column => ({
              ...column,
              sortable: false
            }))}
            paginationMode="server"
            // onPageSizeChange={handlePageSizeChange}
            // pageSize={pageSize}
            rowCount={coursesCount}
            paginationModel={{
              page: paginationModel.page,
              pageSize: paginationModel.pageSize
            }}
            onPaginationModelChange={pagModel => {
              handlePaginationChange(pagModel);
              handlePagination(null, pagModel.page);
            }}

            // onPageChange={(page, details) => handlePagination(details, page)}
          />
        );
      }}
    </GQLGuard>
  );
};
